
// @ts-nocheck
import type { Person, Practitioner } from '../common'
import type { GroupContent, GroupItemContent, VirtuosoHandle } from 'react-virtuoso'

import { keepPreviousData } from '@tanstack/react-query'
import cx from 'classnames'
import { startOfDay } from 'date-fns'
import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react'
import { MdClose, MdNewLabel, MdOutlineArchive, MdOutlineFilterAlt, MdOutlineFilterAltOff } from 'react-icons/md'
import { GroupedVirtuoso, Virtuoso } from 'react-virtuoso'
import { useDebounceValue } from 'usehooks-ts'

import * as passive from '../../../lib/passive-events'
import { useAppSelector } from '../../../store'
import { useSidebar } from '../../layout/context'
import { ChatReadStatus, ChatType, nonNullablePredicate } from '../common'
import { ChatListItem, ChatTypeSelector, MessageComponent, MessageGroupHeader } from '../components'
import { useChatMessagesQuery, useChatParticipantsQuery, useChatsQuery } from '../queries'

let totalMessagesCountDefaultValue = 1e12
let resizeableColumnWidthLocalStorageKey = (userId: string): string => `chats-page:resizeable-column:user-id=${userId}:width`

interface State {
  filters: {
    type: ChatType
    status?: ChatReadStatus
  }

  search: {
    query: string
    isVisible: boolean
  }

  selectedChat?: {
    id: string
  }

  selectedPatient?: {
    id: string
  }

  resizeableColumn: {
    isResizing: boolean
  }
}

let initialState: State = {
  filters: {
    type: ChatType.Own
  },

  search: {
    query: '',
    isVisible: false
  },

  resizeableColumn: {
    isResizing: false
  }
}

type Action =
  | { type: 'filters.type.set', payload: ChatType }
  | { type: 'filters.status.set', payload: ChatReadStatus }
  | { type: 'filters.status.unread.toggle' }
  | { type: 'filters.status.clear' }
  | { type: 'search.query.set', payload: string }
  | { type: 'search.query.clear' }
  | { type: 'selectedChat.set', payload: NonNullable<State['selectedChat']> }
  | { type: 'selectedChat.clear' }
  | { type: 'selectedPatient.set', payload: NonNullable<State['selectedPatient']> }
  | { type: 'selectedPatient.clear' }
  | { type: 'resizeableColumn.isResizing.set', payload: boolean }

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'filters.type.set': {
      return {
        ...state,
        filters: {
          ...state.filters,
          type: action.payload
        },
        selectedPatient: action.payload === ChatType.Current
          ? state.selectedPatient
          : undefined
      }
    }

    case 'filters.status.set': {
      return {
        ...state,
        filters: {
          ...state.filters,
          status: action.payload
        }
      }
    }

    case 'filters.status.clear': {
      return {
        ...state,
        filters: {
          ...state.filters,
          status: undefined
        }
      }
    }

    case 'filters.status.unread.toggle': {
      return {
        ...state,
        filters: {
          ...state.filters,
          status: state.filters.status === ChatReadStatus.Unread
            ? undefined
            : ChatReadStatus.Unread
        }
      }
    }

    case 'search.query.set': {
      return {
        ...state,
        search: {
          ...state.search,
          query: action.payload
        }
      }
    }

    case 'search.query.clear': {
      return {
        ...state,
        search: {
          ...state.search,
          query: ''
        }
      }
    }

    case 'selectedChat.set': {
      return {
        ...state,
        selectedChat: action.payload
      }
    }

    case 'selectedChat.clear': {
      return {
        ...state,
        selectedChat: undefined
      }
    }

    case 'selectedPatient.set': {
      return {
        ...state,
        filters: {
          ...state.filters,
          type: ChatType.Current
        },
        selectedPatient: action.payload
      }
    }

    case 'selectedPatient.clear': {
      return {
        ...state,
        filters: {
          ...state.filters,
          type: ChatType.Own
        },
        selectedPatient: undefined
      }
    }

    case 'resizeableColumn.isResizing.set': {
      return {
        ...state,
        resizeableColumn: {
          ...state.resizeableColumn,
          isResizing: action.payload
        }
      }
    }

    default: {
      return state
    }
  }
}

export function ChatsPage (): JSX.Element {
  let [ state, dispatch ] = useReducer(reducer, initialState)

  let userId = useAppSelector(state => state.auth?.profile?.sub)
  let resizeableColumnWidth = useResizeableColumnWidth({ userId })

  // let chatsQuery = useChatsQuery({
  //   userId,
  //   // patientId,
  //   searchQuery: state.search.query,
  //   status: state.filters.status,
  //   type: state.filters.type
  // })

  // let chatsCount = chatsQuery.data?.pages.reduce((acc, page) => acc + page.length, 0) ?? 0

  // let chatListRef = useRef<VirtuosoHandle>(null)
  // let chatListScrollerRef = useRef<HTMLElement | Window | null>(null)

  let chatTypeOptions = useMemo(() => [
    {
      type: ChatType.Own,
      label: 'My Chats'
      // unreadCount: metaQuery.data?.chats[ChatType.Own]?.unreadCount ?? 0
    },
    {
      type: ChatType.Concierge,
      label: 'Concierge'
      // unreadCount: metaQuery.data?.chats[ChatType.Concierge]?.unreadCount ?? 0
    },
    ...(state.selectedPatient
      ? [{
        type: ChatType.Current,
        label: 'Patient',
        // TODO:
        title: 'Abadam Vale'
        // unreadCount: metaQuery.data?.chats[ChatType.Patient]?.unreadCount ?? 0
      }]
      : []),
    {
      type: ChatType.Archived,
      title: 'Archived chats',
      label: (
        <MdOutlineArchive
          size={20}
          className='-mt-0.5 text-gray-400'
        />
      )
      // unreadCount: metaQuery.data?.chats[ChatType.Archived]?.unreadCount ?? 0
    }
  ], [ state.selectedPatient ])

  let handleChatTypeSelect = useCallback((type: string) => {
    dispatch({
      type: 'filters.type.set',
      payload: type as ChatType
    })
  }, [])

  let handleChatCreate = useCallback(() => {
    // Do nothing
  }, [])

  let handleChatStatusToggle = useCallback(() => {
    dispatch({
      type: 'filters.status.unread.toggle'
    })
  }, [])

  let handleChatSelect = useCallback((id: string) => {
    dispatch({
      type: 'selectedChat.set',
      payload: { id }
    })
  }, [])

  let handleSelectedChatClose = useCallback(() => {
    dispatch({
      type: 'selectedChat.clear'
    })
  }, [])

  let handlePatientSelect = useCallback((id: string) => {
    dispatch({
      type: 'selectedPatient.set',
      payload: { id }
    })
  }, [])

  let handleSearchQueryChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(event => {
    dispatch({
      type: 'search.query.set',
      payload: event.target.value
    })
  }, [])

  let handleResizeableColumnResizeStart = useCallback(() => {
    dispatch({
      type: 'resizeableColumn.isResizing.set',
      payload: true
    })
  }, [])

  let handleResizeableColumnResizeEnd = useCallback((width: number) => {
    dispatch({
      type: 'resizeableColumn.isResizing.set',
      payload: false
    })

    localStorage.setItem(resizeableColumnWidthLocalStorageKey(userId ?? ''), width.toString(10))
  }, [ userId ])

  return (
    <Wrapper
      className={cx(
        'pt-1.5 bg-white',
        state.resizeableColumn.isResizing && 'select-none'
      )}
    >
      <div className='flex flex-row justify-start items-start h-full border-t border-t-gray-200 bg-white'>
        {/* <div className='flex flex-col justify-start items-start h-full w-96 border-r border-r-gray-200'> */}
        <ResizeableColumn
          width={resizeableColumnWidth}
          minWidth={240}
          maxWidth={512}
          className='justify-start items-start h-full border-r border-r-gray-200'
          onResizeStart={handleResizeableColumnResizeStart}
          onResizeEnd={handleResizeableColumnResizeEnd}
        >
          <div className='flex flex-row w-full pl-2 border-b border-b-gray-200'>
            <input
              type='search'
              placeholder='Search'
              className='w-full h-10 px-2 text-base text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500'
              value={state.search.query}
              onChange={handleSearchQueryChange}
            />

            <div className='flex flex-col'>
              <div className='flex flex-row flex-grow justify-start items-center gap-2 p-2'>
                <button
                  type='button'
                  className='p-2 -m-2 flex flex-col items-center justify-center'
                  onClick={handleChatStatusToggle}
                  title={state.filters.status === ChatReadStatus.Unread ? 'Show all chats' : state.filters.status === undefined ? 'Show only unread chats' : undefined}
                >
                  {state.filters.status === ChatReadStatus.Unread && (
                    <MdOutlineFilterAltOff
                      size={22}
                      className='text-blue-600 hover:text-blue-500'
                    />
                  )}

                  {typeof state.filters.status === 'undefined' && (
                    <MdOutlineFilterAlt
                      size={22}
                      className='text-gray-400 hover:text-gray-500'
                    />
                  )}
                </button>

                <button
                  type='button'
                  className='p-2 -m-2 flex flex-col items-center justify-center'
                  onClick={handleChatCreate}
                  title='Create chat'
                >
                  <MdNewLabel
                    size={22}
                    className='text-gray-400 hover:text-gray-500'
                  />
                </button>
              </div>
            </div>
          </div>
          <div className='flex flex-row w-full border-b border-b-gray-200'>
            <ChatTypeSelector
              value={state.filters.type}
              onChange={handleChatTypeSelect}
              options={chatTypeOptions}
            />
          </div>

          <ChatsList
            type={state.filters.type}
            status={state.filters.status}
            patientId={state.selectedPatient?.id}
            selectedChatId={state.selectedChat?.id}
            searchQuery={state.search.query}
            onChatSelect={handleChatSelect}
            onPatientSelect={handlePatientSelect}
          />
          {/* </div> */}
        </ResizeableColumn>

        <div className='flex flex-col flex-grow justify-start items-start h-full'>
          <MessagesList
            chatId={state.selectedChat?.id}
            onClose={handleSelectedChatClose}
          />
        </div>

        {/* <div className='flex flex-col justify-start items-start h-full'>
          <pre>
            {JSON.stringify(state, null, 2)}
          </pre>
        </div> */}
      </div>
    </Wrapper>
  )
}

interface ResizeableColumnProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onResize' | 'onResizeStart' | 'onResizeEnd'> {
  width?: number
  minWidth?: number
  maxWidth?: number

  onResizeStart?: () => void
  onResizeEnd?: (width: number) => void
}

function ResizeableColumn ({
  children,
  className,
  minWidth,
  maxWidth,
  width: initialWidth = 0,
  onResizeStart,
  onResizeEnd,
  ...props
}: ResizeableColumnProps): JSX.Element {
  let [ width, setWidth ] = useState(initialWidth)
  let widthRef = useRef(initialWidth)

  let [ isDragging, setIsDragging ] = useState(false)

  let handleMouseDown = useCallback(() => {
    setIsDragging(true)
  }, [])

  let handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  let handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging) {
      return
    }

    setWidth(width => Math.max(minWidth ?? 0, Math.min(maxWidth ?? Infinity, width + event.movementX)))
  }, [ isDragging, maxWidth, minWidth ])

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp, passive.options)
    window.addEventListener('mousemove', handleMouseMove, passive.options)

    return () => {
      window.removeEventListener('mouseup', handleMouseUp, passive.options)
      window.removeEventListener('mousemove', handleMouseMove, passive.options)
    }
  }, [ handleMouseUp, handleMouseMove ])

  useEffect(() => {
    if (isDragging) {
      onResizeStart?.()
      return
    }

    onResizeEnd?.(widthRef.current)
  }, [ isDragging, onResizeEnd, onResizeStart ])

  useEffect(() => {
    if (!isDragging) {
      return
    }

    let previousValue = document.body.style.cursor
    document.body.style.cursor = 'grabbing'

    return () => {
      document.body.style.cursor = previousValue
    }
  }, [ isDragging ])

  useEffect(() => {
    widthRef.current = width
  }, [ width ])

  return (
    <div
      className={cx('flex flex-col relative', className)}
      style={{
        width,
        minWidth: 0
      }}
      {...props}
    >
      {children}

      { }
      <div
        className={cx(
          'absolute top-0 right-0 h-full px-1 -mx-1 z-10',
          isDragging && 'cursor-grabbing',
          !isDragging && 'cursor-grab'
        )}
        onMouseDown={handleMouseDown}
      />
    </div>
  )
}

interface UseResizeableColumnWidthProps {
  userId?: string
  defaultWidth?: number
}

function useResizeableColumnWidth ({ userId, defaultWidth = 240 }: UseResizeableColumnWidthProps): number {
  let resizeableColumnWidth = useMemo(() => {
    let value = localStorage.getItem(resizeableColumnWidthLocalStorageKey(userId ?? ''))
    let width: number | undefined

    try {
      width = parseInt(value ?? '', 10)
    } catch (err) {}

    return typeof width === 'number'
      ? isNaN(width)
        ? defaultWidth
        : width ?? defaultWidth
      : defaultWidth
  }, [ userId, defaultWidth ])

  return resizeableColumnWidth
}

interface ChatListProps {
  type: ChatType
  status?: ChatReadStatus
  patientId?: string
  selectedChatId?: string
  searchQuery: string
  onChatSelect: (chatId: string) => void
  onPatientSelect: (patientId: string) => void
}

function ChatsList ({
  type,
  status,
  patientId,
  selectedChatId,
  searchQuery,
  onChatSelect,
  onPatientSelect
}: ChatListProps): JSX.Element {
  let userId = useAppSelector(state => state.auth?.profile?.sub)
  let [ debouncedSearchQuery ] = useDebounceValue(searchQuery, 500)

  let query = useChatsQuery({
    userId,
    patientId,
    searchQuery: debouncedSearchQuery,
    status,
    type,
    placeholderData: keepPreviousData
  })

  let chatListRef = useRef<VirtuosoHandle>(null)
  let chatListScrollerRef = useRef<HTMLElement | Window | null>(null)

  if (!query.data) {
    return null
  }

  let count = query.data?.pages?.reduce((acc, page) => Number(acc) + Number(page.length), 0) ?? 0

  return (
    <Virtuoso
      totalCount={count}
      className='w-full flex-grow'
      ref={chatListRef}
      scrollerRef={el => {
        chatListScrollerRef.current = el
      }}
      onScroll={event => {
        // if (state.contextMenu.isVisible) {
        //   event.currentTarget.scrollTo({
        //     top: chatListScrollPosition.current
        //   })

        //   return
        // }

        // chatListScrollPosition.current = event.currentTarget.scrollTop
      }}
      endReached={() => {
        if (query.hasNextPage) {
          void query.fetchNextPage()
        }
      }}
      // overscan={200}
      itemContent={index => {
        let chat = query.data?.pages?.flat()[index]

        if (!chat) {
          return (
            <div>
              <h2>Loading...</h2>
            </div>
          )
        }

        if (!chat.patient) {
          return (
            <div>
              <h2>Chat without patient</h2>
            </div>
          )
        }

        let readAt: Date | undefined
        let patientReadAt: Date | undefined
        let ticks: number | undefined

        if (
          chat.is_concierge && chat.last_message?.author !== `Patient/${chat.patient.id}`
        ) {
          let patientAck = chat?.participants_offsets?.[`Patient/${chat?.patient?.id ?? ''}`]

          readAt = patientAck && patientAck.offset === chat.last_message?.offset
            ? new Date(patientAck.updated_at)
            : undefined

          patientReadAt = readAt

          ticks = readAt && !isNaN(readAt.getTime())
            ? 2
            : undefined
        }

        if (
          !chat.is_concierge &&
          chat.participants?.length === 2 &&
          chat.last_message?.author === `Practitioner/${userId ?? ''}`
        ) {
          let otherParticipantId = chat.participants.find(id => id !== `Practitioner/${userId ?? ''}`)
          let ack = chat?.participants_offsets?.[otherParticipantId ?? '']

          if (ack && ack.offset === chat.last_message?.offset) {
            readAt = new Date(ack.updated_at)
            ticks = 2
          }

          let patientAck = chat?.participants_offsets?.[`Patient/${chat?.patient?.id ?? ''}`]

          patientReadAt = typeof patientAck?.offset === 'number'
            ? new Date(patientAck.offset)
            : undefined
        }

        if (
          !chat.is_concierge &&
          (chat.participants?.length ?? 0) > 2
        ) {
          let patientAck = chat?.participants_offsets?.[`Patient/${chat?.patient?.id ?? ''}`]
          let patientIsParticipant = chat.participants?.some(id => id === `Patient/${chat?.patient?.id ?? ''}`) ?? false
          let patientHasRead = patientIsParticipant && patientAck?.offset === chat?.last_message?.offset

          let nonPatientParticipants = chat.participants?.filter(id => id !== `Patient/${chat?.patient?.id ?? ''}`) ?? []
          let othersHaveRead = nonPatientParticipants.every(id => chat?.participants_offsets?.[id]?.offset === chat?.last_message?.offset)

          patientReadAt = patientHasRead && typeof patientAck?.offset === 'number'
            ? new Date(patientAck.offset)
            : undefined

          if (patientHasRead && !othersHaveRead) {
            readAt = new Date(patientAck?.updated_at ?? '')
            ticks = 1
          }

          if (!patientHasRead && othersHaveRead) {
            let dates = nonPatientParticipants
              .map(id => chat?.participants_offsets?.[id]?.updated_at ?? '')
              .map(date => new Date(date))
              .filter(date => !isNaN(date.getTime()))
              .sort((a, b) => b.getTime() - a.getTime())

            readAt = dates[0]
            ticks = 1

            if (!patientIsParticipant) {
              ticks = 2
            }
          }

          if (patientHasRead && othersHaveRead) {
            let dates = (chat.participants ?? [])
              .map(id => chat?.participants_offsets?.[id]?.updated_at ?? '')
              .map(date => new Date(date))
              .filter(date => !isNaN(date.getTime()))
              .sort((a, b) => b.getTime() - a.getTime())

            readAt = dates[0]
            ticks = 2
          }
        }

        return (
          <ChatListItem
            key={chat.id}
            isActive={/* state.selectedChat?.id === chat.id */ false}
            // readAt={[ 0, 2 ].includes(index) ? new Date() : undefined}
            hasUnreadMessages={(chat?.unread_count ?? 0) > 0}
            isMarkedUnread={chat?.is_marked_unread ?? false}
            isSelected={selectedChatId === chat.id}
            onContextMenu={event => {
              if (!event.isDefaultPrevented()) {
                event.preventDefault()
              }

              if (!event.isPropagationStopped()) {
                event.stopPropagation()
              }

              // @ts-expect-error // TODO: Fix
              onPatientSelect(chat?.patient?.id ?? undefined)

              // if (!chat) {

              // }

              // if (state.contextMenu.isVisible && state.contextMenu.chatId === chat.id) {
              //   dispatch({
              //     type: 'context-menu/hide'
              //   })

              //   return
              // }

              // dispatch({
              //   type: 'context-menu/show',
              //   payload: {
              //     chatId: chat.id,
              //     ...getContextMenuPosition({
              //       event,
              //       container: chatListScrollerRef.current
              //     })
              //   }
              // })
            }}
            onClick={() => {
              if (!chat) {
                return
              }

              onChatSelect(chat.id)
              // onPatientSelect(chat.patient?.id)
            }}
            // @ts-expect-error // TODO: Fix
            patient={chat.patient}
            participants={
              (chat.participants ?? [])
                .map(id => id.split('/')[1] ?? '')
                .concat((chat.last_concierge_message?.concierge || undefined)?.id ?? '')
                .map(id => null)
                .filter(nonNullablePredicate)
            }
            excludedParticipantAvatarIds={chat.is_concierge ? undefined : [ userId ?? '' ]}
            lastMessage={(() => {
              let message = chat.last_message

              if (!message) {
                return undefined
              }

              let author = /* userMap[authorId] ?? */ {
                id: '',
                name: [
                  {
                    given: [ 'N/A' ],
                    family: [ 'N/A' ]
                  }
                ]
              }

              return {
                id: message?.id ?? '',
                author,
                content: message.body ?? 'N/A',
                sentAt: new Date(message?.created_at ?? '')
              }
            })()}

            readAt={readAt}
            patientReadAt={patientReadAt}
            readTicks={ticks}
            isConcierge={chat.is_concierge ?? false}
            tags={chat.meta?.tags ?? undefined}
          />
        )
      }}
    />
  )
}

interface MessagesListProps {
  chatId?: string
  totalCount?: number
  onClose: () => void
}

function MessagesList ({
  chatId,
  totalCount = totalMessagesCountDefaultValue,
  onClose
}: MessagesListProps): JSX.Element {
  let userId = useAppSelector(state => state.auth?.profile?.sub)

  let chatQuery = useChatsQuery({
    id: chatId,
    userId
  })

  let messagesQuery = useChatMessagesQuery({
    chatId,
    userId
  })

  let participantsQuery = useChatParticipantsQuery({
    chatId,
    userId
  })

  let participantsMap = useMemo(() => {
    return participantsQuery.data?.reduce<Record<string, Practitioner>>((acc, participant) => {
      return ({
        ...acc,
        [participant.id]: participant
      })
    }, {}) ?? {}
  }, [ participantsQuery.data ])

  let chat = chatQuery.data?.pages?.flat()?.[0]
  let { data, hasNextPage, fetchNextPage } = messagesQuery
  let { pages } = data ?? {}

  let count = messagesQuery.data?.pages.reduce<number>((acc, page) => Number(acc) + Number(page.length), 0) ?? 0

  let { groups, counts } = useMemo(() => {
    let map = new Map<string, number>()

    for (let page of pages ?? []) {
      for (let message of page) {
        let date = new Date(message.created_at ?? '')

        if (isNaN(date.getTime())) {
          continue
        }

        let key = startOfDay(date).toISOString()
        map.set(key, (map.get(key) ?? 0) + 1)
      }
    }

    let groups = [ ...map.keys() ]
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

    let counts = groups.map(group => map.get(group) ?? 0)

    return {
      groups,
      counts
    }
  }, [ pages ])

  let handleStartReached = useCallback(() => {
    if (!hasNextPage) {
      return
    }

    void fetchNextPage()
  }, [ hasNextPage, fetchNextPage ])

  let groupContent = useCallback<GroupContent<unknown>>(index => {
    let date = new Date(groups[index] ?? '')

    return (
      <MessageGroupHeader>
        {!isNaN(date.getTime()) && (
          date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
        )}

        {isNaN(date.getTime()) && (
          'N/A'
        )}
      </MessageGroupHeader>
    )
  }, [ groups ])

  let itemContent = useCallback<GroupItemContent<unknown, unknown>>((index: number) => {
    let actualIndex = totalCount - index - 1
    let message = messagesQuery.data?.pages?.flat()[actualIndex]

    // let chat = chatQuery.data ?? chatsQuery.data?.pages?.flat().find(chat => chat.id === state.selectedChat?.id)
    let justTheTwoOfUs = chat?.participants?.length === 2 && chat?.participants?.includes(message?.author ?? '')
    let displayAuthor = !justTheTwoOfUs || true

    let senderId = chat?.is_concierge === true && typeof message?.concierge === 'object' && message.concierge !== null
      ? message.concierge.id
      : message?.author?.split('/')[1] ?? ''

    let sender = participantsMap[senderId]
    let [ attachment ] = message?.attachments ?? []

    void attachment

    let concierge = typeof message?.concierge === 'object' && message.concierge !== null ? message.concierge : undefined

    let participantId: string = chat?.concierge
      ? chat?.practitioners?.[0]?.id ?? userId
      : userId

    let isPatient = senderId === chat?.patient?.id
    let isAuthor = typeof userId === 'string' && senderId === userId

    return (
      <MessageComponent
        // @ts-expect-error // It's fine
        author={displayAuthor ? participantsMap[message?.author?.split('/')[1] ?? ''] : undefined}
        concierge={concierge}
        // @ts-expect-error // It's fine
        name={typeof userId === 'string' && userId === senderId ? 'You' : sender ? getName(sender) : 'N/A'}
        isAuthor={isAuthor}
        isPatient={isPatient}
        sentAt={new Date(message?.created_at ?? '')}
        // isBeingSent={sendMessageMutation.isLoading && message?.id.startsWith('local')}
        isBeingSent={false}
        attachments={message?.attachments ?? undefined}
        // @ts-expect-error // It's fine
        chatId={chat?.id}
        // @ts-expect-error // It's fine
        patientId={chat?.patient?.id}
        // @ts-expect-error // It's fine
        participantId={`Practitioner/${participantId ?? ''}`}
        // @ts-expect-error // It's fine
        clientId={chat?.client_id}
        tags={message?.tags ?? undefined}
      >
        {message?.body}
      </MessageComponent>
    )
  }, [ chat?.client_id, chat?.concierge, chat?.id, chat?.is_concierge, chat?.participants, chat?.patient?.id, chat?.practitioners, messagesQuery.data?.pages, participantsMap, totalCount, userId ])

  return (
    <div className='flex flex-col w-full h-full'>
      <div className='flex flex-row flex-shrink-0 h-[84px] border-b border-b-gray-200'>
        {/*  */}
        <span>Chat {chatId}</span>
        <button
          type='button'
          className='ml-auto p-2 -m-2 flex flex-col items-center justify-center'
          onClick={onClose}
          title='Close'
        >
          <MdClose
            size={22}
            className='text-gray-400 hover:text-gray-500'
          />
        </button>
      </div>
      <GroupedVirtuoso
        className='w-full'
        groupCounts={counts}
        firstItemIndex={count > 0 ? Math.max(0, totalCount - count) : 0}
        initialTopMostItemIndex={count > 0 ? count - 1 : 0}
        // followOutput={onFollowOutputHandler}
        startReached={handleStartReached}
        groupContent={groupContent}
        itemContent={itemContent}
      />
    </div>
  )
}

interface UseLayoutMeasuresResult {
  header: {
    height: number
  }

  sidebar: {
    width: number
  }
}

function useLayoutMeasures (): UseLayoutMeasuresResult {
  let [ headerHeight, setHeaderHeight ] = useState(0)
  let [ sidebarWidth, setSidebarWidth ] = useState(0)

  let { state: sidebarState } = useSidebar()
  let sidebarIsOpen = typeof sidebarState === 'string' && sidebarState !== 'closed'

  let measure = useCallback(() => {
    setTimeout(() => {
      let sidebar = document.getElementById('the-sidebar')
      setSidebarWidth(sidebar?.getBoundingClientRect().width ?? 0)
    }, 200)

    let header = document.getElementById('scrollTop')
    setHeaderHeight(header?.getBoundingClientRect().height ?? 0)
  }, [])

  useLayoutEffect(() => {
    measure()

    window.addEventListener('resize', measure, passive.options)

    return () => {
      window.removeEventListener('resize', measure, passive.options)
    }
  }, [ measure, sidebarIsOpen ])

  let value = useMemo(() => ({
    header: {
      height: headerHeight
    },

    sidebar: {
      width: sidebarWidth
    }
  }), [ headerHeight, sidebarWidth ])

  return value
}

function useHeaderStyleSetter (): void {
  useEffect(() => {
    const header = document.getElementById('scrollTop')

    if (!header) {
      return
    }

    let values: Partial<CSSStyleDeclaration> = {
      backgroundColor: 'white',
      width: 'calc(100% - 1rem)',
      borderTopRightRadius: '2rem'
    }

    let previousValues = Object.keys(values).reduce((acc, key) => ({ ...acc, [key]: header.style[key as keyof typeof values] }), {})
    Object.assign(header.style, values)

    return () => {
      Object.assign(header.style, previousValues)
    }
  }, [])
}

interface WrapperProps extends React.HTMLAttributes<HTMLDivElement> {}

function Wrapper ({ children, className, style, ...props }: WrapperProps): JSX.Element {
  let measures = useLayoutMeasures()
  useHeaderStyleSetter()

  return (
    <div
      className={(cx('tailwind', className))}
      style={{
        width: 'calc(100% - 1rem)',
        height: `calc(100vh - ${measures.header.height + 6}px)`,
        overflow: 'hidden',
        borderBottomRightRadius: '2rem',
        ...style
      }}
      {...props}
    >
      {children}
    </div>
  )
}

function getName (person: Person): string {
  // TODO: Fix
  if (!person) {
    return 'N/A'
  }

  return [
    person.name[0]?.given?.[0],
    person.name[0]?.family?.[0]
  ]
    .filter(Boolean)
    .join(' ')
}
